/* eyeclub-a11y.js
 *
 * Mirrors aria-hidden onto inert for carousel slides.
 *
 * WHY
 * Flickity marks off-screen slides aria-hidden="true", which hides them from
 * assistive tech but leaves their links and buttons in the tab order. A
 * keyboard user tabbing through a product page walks into six invisible
 * product cards before reaching anything on screen. Lighthouse reports it as
 * aria-hidden-focus; axe treats it as a violation because an element cannot
 * be both hidden from the accessibility tree and reachable by keyboard.
 *
 * WHY inert RATHER THAN tabindex
 * inert is one property on the slide, so there is no per-descendant state to
 * track and restore as Flickity toggles slides during a swipe. Juggling
 * tabindex means remembering each element's original value, and getting that
 * wrong makes something permanently unfocusable.
 *
 * WHY THIS IS SAFE HERE, MEASURED NOT ASSUMED
 * inert also blocks pointer events, so it would break a slide that is hidden
 * from AT while still partly visible, which is what a "peeking" carousel
 * does. On this theme they are fully off-screen. Lighthouse bounding rects on
 * the product page, 412px viewport:
 *
 *     left -1236 .. right -1030
 *     left -1030 .. right  -824
 *     ...
 *     left  -206 .. right     0
 *
 * Every hidden slide ends at or before x=0. None intrudes into the viewport,
 * so nothing clickable becomes unclickable. If the carousel is ever changed
 * to peek, re-check this before trusting it.
 *
 * Scoped to .flickitybee-slider so it cannot touch aria-hidden anywhere else,
 * where the attribute may be doing a different job.
 */
(function () {
  'use strict';

  // No-op on browsers without inert rather than falling back to a tabindex
  // scheme that is harder to get right. Doing nothing is not a regression.
  if (!('inert' in HTMLElement.prototype)) return;

  var SLIDER = '.flickitybee-slider';

  function sync(slide) {
    var hidden = slide.getAttribute('aria-hidden') === 'true';
    if (slide.inert !== hidden) slide.inert = hidden;
  }

  function syncAll(root) {
    (root || document).querySelectorAll(SLIDER + ' > [aria-hidden]').forEach(sync);
  }

  function watch(slider) {
    if (slider.dataset.eycInertWatched) return;
    slider.dataset.eycInertWatched = '1';
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        if (r.type === 'attributes' && r.target.parentElement === slider) {
          sync(r.target);
        } else if (r.type === 'childList') {
          syncAll(slider.parentElement || document);
        }
      });
    }).observe(slider, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
      childList: true
    });
    syncAll(slider.parentElement || document);
  }

  function init() {
    document.querySelectorAll(SLIDER).forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Flickity is initialised lazily on some sections, so pick up sliders that
  // appear after first paint. Disconnects once the page settles.
  var late = new MutationObserver(init);
  late.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', function () {
    setTimeout(function () { late.disconnect(); init(); }, 3000);
  });
})();
